import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Bot, User, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = localStorage.getItem("chat_session_id");
  if (!sid) {
    sid = `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("chat_session_id", sid);
  }
  return sid;
}

const QUICK_QUESTIONS = [
  "What plot sizes are available?",
  "What is the price per sqmtr?",
  "How to book a plot?",
  "What govt schemes are available?",
];

export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Hello! I'm the VSINDLPARK.COM AI assistant. Ask me anything about our industrial plots at IIE Sigaddi, Kotdwar.\n\nHow can I help you?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, open]);

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages((prev) => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        message: msg,
        session_id: sessionId.current,
      });
      setMessages((prev) => [...prev, { role: "assistant", text: res.data.response }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", text: "Sorry, I couldn't respond. Please call us at +91-9258959058 or +91-9557877290." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-4 z-40 w-80 sm:w-96 bg-[#1E293B] shadow-2xl border border-white/10 flex flex-col"
          style={{ height: "500px", maxHeight: "80vh" }}
          data-testid="ai-chat-widget"
        >
          {/* Header */}
          <div className="bg-slate-900 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 bg-orange-500 flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div>
                <div className="text-white text-sm font-bold">VSINDLPARK AI Assistant</div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                  <span className="text-emerald-400 text-xs">Online</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-white transition-colors"
              data-testid="chat-close-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#0F172A]">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}
              >
                <div
                  className={`w-6 h-6 flex-shrink-0 flex items-center justify-center mt-0.5 ${
                    msg.role === "user" ? "bg-orange-500" : "bg-slate-700"
                  }`}
                >
                  {msg.role === "user" ? (
                    <User className="w-3.5 h-3.5 text-white" />
                  ) : (
                    <Bot className="w-3.5 h-3.5 text-white" />
                  )}
                </div>
                <div
                  className={`max-w-[80%] px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === "user"
                      ? "bg-orange-500 text-white"
                      : "bg-slate-800 text-slate-200 border border-white/10"
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex gap-2">
                <div className="w-6 h-6 bg-slate-700 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-slate-800 border border-white/10 px-3 py-2 flex items-center gap-1">
                  <Loader2 className="w-3.5 h-3.5 text-slate-400 animate-spin" />
                  <span className="text-slate-400 text-xs">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Questions */}
          {messages.length <= 1 && (
            <div className="px-3 py-2 border-t border-white/10 bg-[#1E293B] flex-shrink-0">
              <p className="text-xs text-slate-500 mb-1.5">Quick questions:</p>
              <div className="flex flex-wrap gap-1.5">
                {QUICK_QUESTIONS.map((q) => (
                  <button
                    key={q}
                    onClick={() => sendMessage(q)}
                    className="text-xs bg-slate-800 hover:bg-orange-500/10 hover:text-orange-400 text-slate-400 px-2 py-1 border border-white/10 hover:border-orange-500/40 transition-colors"
                    data-testid={`quick-question-${q.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-white/10 bg-[#1E293B] flex-shrink-0">
            <div className="flex gap-2">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask about plots, pricing, booking..."
                rows={1}
                className="flex-1 border border-slate-600 px-3 py-2 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500 resize-none"
                data-testid="chat-input"
              />
              <button
                onClick={() => sendMessage()}
                disabled={!input.trim() || loading}
                className="bg-orange-500 hover:bg-orange-600 text-white p-2 transition-colors disabled:opacity-50"
                data-testid="chat-send-btn"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-slate-400 mt-1.5 text-center">
              Or call: <a href="tel:+919258959058" className="text-orange-500">+91-9258959058</a>
            </p>
          </div>
        </div>
      )}

      {/* Toggle Button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-4 right-4 z-40 w-14 h-14 shadow-xl flex items-center justify-center transition-all duration-300 ${
          open ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-900 hover:bg-slate-700"
        }`}
        data-testid="ai-chat-toggle-btn"
        title="Chat with AI Assistant"
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <MessageCircle className="w-6 h-6 text-white" />
        )}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-orange-500 text-white text-xs flex items-center justify-center font-bold">
            AI
          </span>
        )}
      </button>
    </>
  );
}
